/**
 * Limits how often the counters are refreshed:
 * - all triggers within `delayMs` -> one refresh
 * - at least `minIntervalMs` between two refreshes
 * - never two refreshes at the same time: triggers during a refresh -> one refresh after it
 */
export const createThrottle = (refresh: () => Promise<unknown>, delayMs = 500, minIntervalMs = 3000) => {
    let timer: ReturnType<typeof setTimeout> | null = null
    let lastRefresh = 0
    let running = false
    let pending = false

    const trigger = () => {
        if (running) {
            pending = true // refresh again when the running one is done
            return
        }
        if (timer) return // a refresh is already planned

        const wait = Math.max(delayMs, lastRefresh + minIntervalMs - Date.now())
        timer = setTimeout(run, wait)
    }

    const run = () => {
        timer = null
        lastRefresh = Date.now()
        running = true

        refresh()
            .catch(() => {}) // errors are shown by useAPIRequest
            .finally(() => {
                running = false
                if (!pending) return

                pending = false
                trigger()
            })
    }

    const cancel = () => {
        if (timer) clearTimeout(timer)
        timer = null
        pending = false
    }

    return { trigger, cancel }
}
